import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import surveyService from '../../services/surveyService';
import Icon from '../../components/AppIcon';

const SurveyPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [templates, setTemplates] = useState([]);
  
  // Dados do formulário
  const [overallRating, setOverallRating] = useState(0);
  const [questionRatings, setQuestionRatings] = useState({});
  const [comments, setComments] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      const data = await surveyService.getSurveyInvitation(token);
      
      console.log('📋 Dados recebidos da API:', data);
      console.log('📋 surveyTemplates recebido:', data.surveyTemplates);
      console.log('📋 Keys do objeto:', Object.keys(data));
      console.log('📋 reservation?.hotel:', data.reservation?.hotel);
      console.log('📋 reservation?.hotel?.id:', data.reservation?.hotel?.id);
      
      if (data.message) {
        setSubmitted(true);
      }
      
      setInvitation(data);
      
      // Carregar templates do hotel (perguntas parametrizadas)
      if (data.surveyTemplates && Array.isArray(data.surveyTemplates)) {
        console.log(`📋 ${data.surveyTemplates.length} templates encontrados`);
        setTemplates(data.surveyTemplates);
      } else {
        console.log('⚠️ Nenhum template encontrado ou formato inválido');
        setTemplates([]);
      }
      
      // Se já foi completado, marcar como submitted
      if (data.isCompleted) {
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Erro ao carregar convite:', err);
      setError(err.response?.data?.error || 'Link de avaliação inválido ou expirado');
    } finally {
      setLoading(false);
    }
  };

  const handleStarClick = (rating, questionId = null) => {
    if (questionId) {
      setQuestionRatings(prev => ({
        ...prev,
        [questionId]: rating
      }));
    } else {
      setOverallRating(rating);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (overallRating === 0) {
      alert('Por favor, dê uma avaliação geral');
      return;
    }

    try {
      setSubmitting(true);
      await surveyService.submitSurveyResponse(token, {
        overallRating,
        questionRatings,
        comments,
        wouldRecommend
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Erro ao enviar avaliação:', err);
      alert('Erro ao enviar avaliação. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
          <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={40} className="text-destructive" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Link Inválido</h2>
          <p className="text-slate-600">{error || 'Este link de avaliação não é válido.'}</p>
        </div>
      </div>
    );
  }

  if (submitted || invitation.isCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200 p-8 text-center">
          <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircle" size={40} className="text-success" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">Obrigado pela sua Avaliação!</h2>
          <p className="text-slate-600 mb-6">
            Sua opinião é muito importante para nós e nos ajuda a melhorar continuamente.
          </p>
          <div className="w-16 h-1 bg-primary rounded-full mx-auto"></div>
        </div>
      </div>
    );
  }

  const reservation = invitation.reservation;
  const hotel = reservation?.hotel;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-4 md:py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Logo e Header com gradiente */}
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-xl border border-primary/20 p-4 md:p-6 mb-4 shadow-sm">
          {hotel?.logo && (
            <div className="mb-3">
              <img 
                src={hotel.logo} 
                alt={hotel.name}
                className="h-12 md:h-16 mx-auto object-contain"
              />
            </div>
          )}
          
          <div className="text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2">
            Avaliação de Satisfação
          </h1>
            <p className="text-base text-slate-600 mb-3">
              Olá <strong className="text-primary">{reservation?.guestName}</strong>
            </p>
            
            <div className="inline-flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full">
              <Icon name="Heart" size={14} className="text-primary" />
              <span className="text-xs font-medium text-primary">
                Agradecemos pela sua estadia!
              </span>
            </div>
          </div>
        </div>

        {/* Cards com informações da reserva */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 md:p-4">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Building" size={16} className="text-primary" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Hotel</p>
                <p className="text-sm font-bold text-slate-900">{hotel?.name}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-3 md:p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Bed" size={16} className="text-success" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Quarto</p>
                <p className="text-sm font-bold text-slate-900">{reservation?.room?.number}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form principal */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg border border-slate-200 p-5 md:p-6 space-y-6">
          {/* Avaliação Geral */}
          <div className="space-y-3">
            <label className="block text-base font-semibold text-slate-900 mb-3">
              Como você avalia sua experiência geral? 
              <span className="text-primary ml-1">*</span>
            </label>
            
            {/* Estrelas responsivas */}
            <div className="flex justify-center md:justify-start gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleStarClick(star)}
                  className={`p-2.5 rounded-lg transition-all transform hover:scale-110 active:scale-95 ${
                    star <= overallRating
                      ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-white'
                      : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                  }`}
                >
                  <Icon name="Star" size={24} className="fill-current" />
                </button>
              ))}
            </div>
            
            {/* Feedback visual */}
            {overallRating > 0 && (
              <div className="mt-2 text-center md:text-left">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary font-medium text-sm">
                  {overallRating === 5 && (
                    <>
                      <Icon name="Sparkles" size={18} />
                      Excelente!
                    </>
                  )}
                  {overallRating === 4 && (
                    <>
                      <Icon name="ThumbsUp" size={18} />
                      Muito Bom!
                    </>
                  )}
                  {overallRating === 3 && <>Bom</>}
                  {overallRating === 2 && <>Regular</>}
                  {overallRating === 1 && (
                    <>
                      <Icon name="AlertCircle" size={18} />
                      Ruim
                    </>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Perguntas Específicas */}
          <div className="space-y-4">
            <h3 className="text-base font-semibold text-slate-900 border-b border-slate-200 pb-2">
              Avaliação Detalhada
            </h3>
            
          {templates.map((template) => (
              <div key={template.id} className="bg-slate-50 rounded-lg p-3 md:p-4 space-y-2">
                <label className="block text-sm font-medium text-slate-900">
                {template.question}
              </label>
                
                <div className="flex justify-center md:justify-start gap-1.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleStarClick(star, template.id)}
                      className={`p-2 rounded-lg transition-all transform hover:scale-110 active:scale-95 ${
                      star <= (questionRatings[template.id] || 0)
                          ? 'bg-secondary text-white'
                          : 'bg-white text-slate-400 border border-slate-200 hover:border-secondary'
                    }`}
                  >
                      <Icon name="Star" size={18} className="fill-current" />
                  </button>
                ))}
              </div>
            </div>
          ))}
          </div>

          {/* Recomendação */}
          <div className="space-y-3">
            <label className="block text-base font-semibold text-slate-900">
              Você recomendaria nosso hotel?
            </label>
            
            <div className="grid grid-cols-2 gap-2 md:gap-3">
              <button
                type="button"
                onClick={() => setWouldRecommend(true)}
                className={`py-3 px-4 rounded-lg border-2 transition-all transform hover:scale-105 active:scale-95 ${
                  wouldRecommend === true
                    ? 'border-success bg-success/10 text-success'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-success hover:bg-success/5'
                }`}
              >
                <Icon name="ThumbsUp" size={20} className="mx-auto mb-1.5" />
                <span className="text-sm font-semibold">Sim</span>
              </button>
              
              <button
                type="button"
                onClick={() => setWouldRecommend(false)}
                className={`py-3 px-4 rounded-lg border-2 transition-all transform hover:scale-105 active:scale-95 ${
                  wouldRecommend === false
                    ? 'border-destructive bg-destructive/10 text-destructive'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-destructive hover:bg-destructive/5'
                }`}
              >
                <Icon name="ThumbsDown" size={20} className="mx-auto mb-1.5" />
                <span className="text-sm font-semibold">Não</span>
              </button>
            </div>
          </div>

          {/* Comentários */}
          <div className="space-y-2">
            <label htmlFor="comments" className="block text-base font-semibold text-slate-900">
              Comentários Adicionais
              <span className="text-slate-400 text-sm font-normal ml-2">(Opcional)</span>
            </label>
            
            <textarea
              id="comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={4}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-lg bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
              placeholder="Compartilhe sua experiência, sugestões ou melhorias..."
            />
            
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || overallRating === 0}
            className="w-full py-3 px-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Enviando...
              </span>
            ) : (
              'Enviar Avaliação'
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-4 text-center space-y-1">
          <p className="text-xs text-slate-500">
          Sua avaliação é confidencial e nos ajuda a melhorar nossos serviços
        </p>
        </div>
      </div>
    </div>
  );
};

export default SurveyPage;